import type {
  OpsActionState,
  OpsAgeBand,
  OpsCloseReason,
  OpsConfirmerRule,
  OpsEscalationState,
  OpsHistoryEvent,
  OpsIssueRow,
  OpsIssueState,
  OpsOrigin,
  OpsRefusal,
  OpsSeverity,
  OpsSourceKind,
} from '@ucp/web/lib';
import { isOfstedOrigin } from '../../lib/ops/opsAuthority';
import { OPS_NAME_NOT_RECORDED } from '../../lib/ops/opsCopy';

export {
  OPS_FREE_TEXT_RULE,
  OPS_LEVEL_LABEL,
  OPS_LIMITS,
  OPS_NAME_NOT_RECORDED,
  OPS_REQUEST_TYPE_LABEL,
  OPS_THRESHOLD_RANGE,
} from '../../lib/ops/opsCopy';

/** Labels, fixed messages and London-time formatting for the Weekly Report SPA (PRO-3100). */

export const WEEKLY_REPORT_BASE = '/weekly-report';

export const OPS_STATE_LABEL: Readonly<Record<OpsIssueState, string>> = {
  open: 'Open',
  monitoring: 'Monitoring',
  resolution_proposed: 'Awaiting confirmation',
  resolved: 'Resolved',
};

export const OPS_SEVERITY_LABEL: Readonly<Record<OpsSeverity, string>> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export const OPS_AGE_BAND_LABEL: Readonly<Record<OpsAgeBand, string>> = {
  new: 'New',
  established: 'Established',
  long_running: 'Long-running',
};

export const OPS_ACTION_STATE_LABEL: Readonly<Record<OpsActionState, string>> = {
  open: 'Open',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const OPS_ESCALATION_STATE_LABEL: Readonly<Record<OpsEscalationState, string>> = {
  raised: 'Raised',
  acknowledged: 'Acknowledged',
  responded: 'Responded',
  closed: 'Closed',
  withdrawn: 'Withdrawn',
};

export const OPS_CLOSE_REASON_LABEL: Readonly<Record<OpsCloseReason, string>> = {
  escalator_closed: 'Closed by the person who escalated',
  issue_resolved: 'Closed when the issue was resolved',
};

export const OPS_SOURCE_KIND_LABEL: Readonly<Record<OpsSourceKind, string>> = {
  complaint: 'Complaint',
  atr_request: 'Approval to recruit request',
};

/** PRO-3110 TIP-D10: a review-raised issue shows its origin label only, no source panel. */
export const OPS_ORIGIN_LABEL: Readonly<Partial<Record<OpsOrigin, string>>> = {
  weekly_review: 'Weekly review',
  ofsted_readiness: 'Ofsted readiness',
  ofsted_inspection: 'Ofsted inspection',
  trip_review: 'Post-visit review',
};

export const OPS_CONFIRMER_RULE_TEXT: Readonly<Record<OpsConfirmerRule, string>> = {
  od: 'The Operations Director for this provision confirms resolution.',
  coo_fallback: 'This provision has no Operations Director, so the COO confirms resolution.',
  ceo_fallback: 'This provision has no Operations Director or COO, so the CEO confirms resolution.',
  ofsted_qa: 'A Group QA lead confirms resolution of an Ofsted readiness gap.',
  none: 'Nobody can confirm resolution at this provision yet. Ask for an Operations Director to be assigned.',
};

/** PRO-3140 TIP-D13: an Ofsted-origin issue never goes to the Operations Director. */
export const OPS_OFSTED_CONFIRMER_RULE_TEXT: Readonly<Record<OpsConfirmerRule, string>> = {
  ...OPS_CONFIRMER_RULE_TEXT,
  coo_fallback: 'No other Group QA lead can confirm this gap, so the COO confirms resolution.',
  ceo_fallback:
    'No other Group QA lead or COO can confirm this gap, so the CEO confirms resolution.',
  none: 'Nobody can confirm this Ofsted readiness gap yet. Ask for a second Group QA lead to be assigned.',
};

export function confirmerRuleText(rule: OpsConfirmerRule, origin: OpsOrigin): string {
  return (isOfstedOrigin(origin) ? OPS_OFSTED_CONFIRMER_RULE_TEXT : OPS_CONFIRMER_RULE_TEXT)[rule];
}

export const OPS_EVENT_LABEL: Readonly<Record<OpsHistoryEvent, string>> = {
  created: 'Raised',
  updated: 'Update',
  proposed: 'Resolution proposed',
  withdrawn: 'Proposal withdrawn',
  returned: 'Proposal returned',
  confirmed: 'Resolution confirmed',
  reopened: 'Reopened',
  action_created: 'Action added',
  action_completed: 'Action completed',
  action_redated: 'Action re-dated',
  action_cancelled: 'Action cancelled',
  action_reassigned: 'Action reassigned',
  escalation_raised: 'Escalated',
  escalation_acknowledged: 'Escalation acknowledged',
  escalation_responded: 'Escalation responded',
  escalation_closed: 'Escalation closed',
  escalation_withdrawn: 'Escalation withdrawn',
  source_linked: 'Linked to a record',
};

export const OPS_ERROR = {
  noSchool: 'Choose a school in the header to see its operational issues.',
  network: 'We could not reach TAG Operations. Check your connection and try again.',
  reauth: 'Your sign-in needs refreshing. Try again.',
  invalid: 'Check the highlighted field and try again.',
  forbidden: 'You do not have permission to do that here.',
  notFound: 'We could not find that issue at this school.',
  conflict: 'Someone else changed this issue. Reload it and try again.',
  rateLimited: 'Too many lookups in a short time. Wait a minute and try again.',
  unavailable: 'Operational issues are temporarily unavailable. Try again shortly.',
  failed: 'Something went wrong. Try again.',
} as const;

/** Fixed copy for each coded refusal (never server text that might echo input). */
export const OPS_REFUSAL_MESSAGE: Readonly<Partial<Record<OpsRefusal, string>>> = {
  OWNER_NOT_ELIGIBLE: 'That person cannot own this. Choose someone from the list.',
  category_inactive: 'That category is no longer in use. Choose another.',
  not_confirmer: 'You cannot confirm resolution at this provision.',
  self_confirmation: 'You proposed this resolution, so someone else must confirm it.',
  not_responder: 'You cannot respond to this escalation.',
  self_response: 'You raised this escalation, so someone else must respond.',
  not_proposer: 'Only the person who proposed the resolution can withdraw it.',
  not_escalator: 'Only the person who escalated can do that.',
  not_owner: 'Only the action owner or a manager can do that.',
  stale_state: 'Someone else changed this issue. Reload it and try again.',
  escalation_live: 'This issue already has a live escalation.',
  issue_proposed: 'Resolution is already proposed. Withdraw or return it first.',
  issue_not_active: 'This issue is not open or monitoring.',
  action_not_open: 'This action is no longer open.',
  open_actions:
    'This issue still has open actions. Choose to leave them open, or finish them first.',
  escalation_not_live: 'This escalation is no longer live.',
  already_responded: 'This escalation already has a response.',
  integrity_failed: 'The export could not be verified. Ask IT to check the audit chain.',
};

/** The text badges for one row (TIP §5): meaning is always in the words, never colour alone. */
export function issueBadges(row: OpsIssueRow): string[] {
  const badges: string[] = [];
  if (row.newThisWeek) badges.push('New this week');
  if (row.longRunning) badges.push('Long-running');
  if (row.escalation && ['raised', 'acknowledged', 'responded'].includes(row.escalation.state)) {
    badges.push('Escalated');
  }
  if (row.overdueActionCount > 0) badges.push('Overdue');
  if (row.state === 'resolution_proposed') badges.push('Awaiting confirmation');
  if (row.confirmationOverdue) badges.push('Confirmation overdue');
  if (row.escalation?.responseOverdue) badges.push('Response overdue');
  if (!row.ownerHasAccess) badges.push('Owner no longer has access');
  return badges;
}

export function nameOrNotRecorded(name: string | null): string {
  return name ?? OPS_NAME_NOT_RECORDED;
}

const LONDON_DATE = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const LONDON_DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** `YYYY-MM-DD` (a London calendar date) → "6 Oct 2026". */
export function formatOpsDate(date: string | null): string {
  if (!date) return '—';
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  return LONDON_DATE.format(new Date(Date.UTC(y, m - 1, d, 12)));
}

/** ISO instant → "6 Oct 2026, 09:00" in London time. */
export function formatOpsInstant(iso: string | null): string {
  if (!iso) return '—';
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '—' : LONDON_DATE_TIME.format(at);
}

export function issuePath(schoolId: string, issueId: string): string {
  return `${WEEKLY_REPORT_BASE}/issues/${encodeURIComponent(schoolId)}/${encodeURIComponent(issueId)}`;
}
